import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { useAuthStore } from '../stores/authStore';
import { useCapabilitiesStore } from '../stores/capabilitiesStore';
import { useToast } from '../components/ui/Toast';
import { Card, CardContent, CardDescription, CardHeader } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Button } from '../components/ui/Button';
import { ThemeToggle } from '../components/ui/ThemeToggle';
import { MdsLogo } from '../components/MdsLogo';
import type { Role } from '../auth/roles';

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: string;
    email: string;
    role: Role;
    customerId: string | null;
    name: string | null;
    mustChangePassword: boolean;
  };
}

const STRONG = (p: string): boolean =>
  p.length >= 8 && /[A-Z]/.test(p) && /[0-9]/.test(p) && /[^A-Za-z0-9]/.test(p);

/**
 * Forced password change — shown at first login of a bootstrap / admin-provisioned / reset
 * account (screens 41-42, Section 27.17). Until this succeeds the API refuses every other
 * route with 403 PASSWORD_CHANGE_REQUIRED, so this screen is the only way forward. A
 * successful change returns a fresh session (token without the forced-change claim).
 */
export default function ForcePasswordChange() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const email = useAuthStore((s) => s.user?.email);
  const fetchCaps = useCapabilitiesStore((s) => s.fetch);
  const { showApiError, push } = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next !== confirm) {
      push({ tone: 'warning', message: t('auth.force.mismatch') });
      return;
    }
    if (!STRONG(next)) {
      push({ tone: 'warning', message: t('auth.force.tooWeak') });
      return;
    }
    setBusy(true);
    try {
      const { data } = await apiClient.post<{ data: LoginResponse }>('/me/password', {
        currentPassword: current,
        newPassword: next,
      });
      const r = data.data;
      localStorage.setItem('refresh_token', r.refreshToken);
      setSession(r.accessToken, {
        sub: r.user.id,
        role: r.user.role,
        customerId: r.user.customerId,
        email: r.user.email,
        mustChangePassword: r.user.mustChangePassword,
      });
      await fetchCaps();
      push({ tone: 'success', message: t('auth.force.updated') });
      navigate('/');
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-canvas p-4">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-2 text-center">
          <div className="mds flex justify-center">
            <MdsLogo />
          </div>
          <CardDescription>
            {t('auth.force.subtitle')}
            {email ? <span className="block text-xs mt-1">{email}</span> : null}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current">{t('auth.force.current')}</Label>
              <Input
                id="current"
                type="password"
                required
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="next">{t('auth.force.new')}</Label>
              <Input
                id="next"
                type="password"
                required
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">{t('auth.force.hint')}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm">{t('auth.force.confirm')}</Label>
              <Input
                id="confirm"
                type="password"
                required
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={busy} className="w-full">
              <KeyRound className="h-4 w-4" />
              {busy ? t('common.saving') : t('auth.force.submit')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
